import { z } from "zod";

/**
 * Le contrat de l'agent du PC (`packages/atelier`, sur le PC de Nicolas) : ce qu'il envoie a
 * Castellan et ce qu'il en recoit (`/api/atelier/*`, backend `atelier/routes.ts`), et l'etat que la
 * regie lit pour sa carte « Agent du PC » (`GET /api/diffusion/atelier`). Les deux cotes le lisent
 * par leur pont `src/app/contract.ts`.
 *
 * Il vit ici, et non dans `contract.ts` de ce paquet, parce que `src/contract.ts` appartient au
 * template : l'app possede `src/app/**`.
 *
 * Les textes qui partent vers le PC (les messages d'erreur) sont sans accent ni apostrophe, comme
 * ceux de la console : le PC les affiche tels quels. Aucun schema ici ne rend une valeur recue.
 */

/**
 * Les types de tache de la file : ceux de la contrainte de `atelier_taches.type`
 * (`migrations/1004_atelier.sql`), que `atelier/routes.test.ts` garde alignes. La diffusion
 * (inventaire, televersement, programmation), puis l'atelier des clips (recolte, purge).
 */
export const TYPES_TACHE = [
  "inventaire",
  "televersement",
  "programmation",
  "recolte",
  "purge",
] as const;
export const typeTacheSchema = z.enum(TYPES_TACHE);

/** Les etats d'une tache, ceux de la contrainte de `atelier_taches.etat`. */
export const ETATS_TACHE = ["a_faire", "en_cours", "faite", "echec"] as const;
export const etatTacheSchema = z.enum(ETATS_TACHE);

/**
 * Un texte court sur une seule ligne : la version de l'agent, son etat. Aucun caractere de la categorie
 * Unicode « autres » (`\p{C}` : controle comme le retour a la ligne ou NUL, formatage invisible comme la
 * surcharge bidi U+202E ou l'espace de largeur nulle U+200B, usage prive, point non attribue, moitie de
 * paire de substitution) ni separateur de ligne ou de paragraphe, ecrits par categorie et non par point
 * de code : c'est la classe du reste de l'app. Une valeur qui n'est pas un texte de la base (un NUL) la
 * ferait lever ; un texte que la regie affiche ne doit pas se lire a l'envers ni porter de caractere
 * invisible.
 */
const UNE_LIGNE = /^[^\p{C}\p{Zl}\p{Zp}]+$/u;
const texteCourt = z.string().min(1).max(100).regex(UNE_LIGNE);

/**
 * Le corps de `POST /api/atelier/ping` : le signe de vie. `detail`, s'il est donne, est un texte libre
 * que la file rogne et ramene a une seule ligne de 300 caracteres au plus ; `null` vaut absent.
 */
export const pingSchema = z.object({
  version: texteCourt,
  etat: texteCourt,
  detail: z.string().nullish(),
});
export type Ping = z.infer<typeof pingSchema>;

/** Le corps de `POST /api/atelier/tache` : la version de l'agent, et les types qu'il sait faire. */
export const demandeTacheSchema = z.object({
  version: texteCourt,
  types: z.array(typeTacheSchema).min(1),
});
export type DemandeTache = z.infer<typeof demandeTacheSchema>;

/** Ce que l'agent recoit d'une tache pour la faire. */
export const tacheSchema = z.object({
  id: z.string(),
  type: typeTacheSchema,
  cle: z.string(),
  entree: z.record(z.string(), z.unknown()),
});
export type TacheContrat = z.infer<typeof tacheSchema>;

/** La reponse de `POST /api/atelier/tache` : la tache prise, ou `null` (rien a faire, ou un direct ouvert). */
export const reponseTacheSchema = z.object({ tache: tacheSchema.nullable() });

/** Ce que l'agent a produit en faisant une tache : un objet JSON, de la forme que le type de tache attend. */
export const sortieSchema = z.record(z.string(), z.unknown());

/** Le corps de `POST /api/atelier/tache/{id}/faite`. */
export const faiteSchema = z.object({ sortie: sortieSchema });

/**
 * Le corps de `POST /api/atelier/tache/{id}/echec` : un code court ecrit par l'agent (`fichier_absent`),
 * que la file ramene a une seule ligne de 300 caracteres au plus et que la regie affiche tel quel.
 */
export const causeSchema = z.object({ cause: z.string() });

/**
 * Un type MIME : un type en minuscules, une barre, un sous-type de 1 a 60 caracteres. Borne parce
 * que la valeur reviendra un jour en `Content-Type` : rien d'autre ne la controle.
 */
const TYPE_MIME = /^[a-z]+\/[a-z0-9.+-]{1,60}$/;

/**
 * Le corps de `POST /api/atelier/fichier`. Le nom, la taille et l'empreinte sont controles par le
 * magasin des fichiers (`nom_invalide`, `taille_invalide`, `trop_lourd`, `empreinte_invalide`), qui
 * dit en quoi l'un d'eux est refuse : le nom suit `[A-Za-z0-9._ -]{1,200}` (ni `.` ni `..`), la taille
 * est un entier de 1 a 300 Mo, l'empreinte un SHA-256 en 64 chiffres hexadecimaux minuscules.
 */
export const ouvertureFichierSchema = z.object({
  nom: z.string(),
  typeMime: z.string().regex(TYPE_MIME),
  taille: z.number(),
  empreinte: z.string(),
});
export type OuvertureFichier = z.infer<typeof ouvertureFichierSchema>;

/**
 * Un fichier en cours d'envoi, ou complet. `recu` est le nombre d'octets deja gardes : l'agent reprend
 * a `recu / 1500000`, le rang du prochain morceau.
 */
export const fichierSchema = z.object({
  id: z.string(),
  nom: z.string(),
  typeMime: z.string(),
  taille: z.number(),
  empreinte: z.string(),
  recu: z.number(),
  complet: z.boolean(),
});
export type FichierContrat = z.infer<typeof fichierSchema>;

/** La reponse de `POST /api/atelier/fichier` et de `POST /api/atelier/fichier/{id}/morceau/{rang}`. */
export const reponseFichierSchema = z.object({ fichier: fichierSchema });

// --- L'envoi d'un best-of long sur YouTube ----------------------------------------------------------------

/**
 * Le contrat de l'envoi d'un best-of long sur YouTube par l'atelier du PC (plan
 * `docs/plans/2026-10-26-castellan-bestof-envoi.md`, spec des clips, section 8 « L'envoi »). Castellan ne
 * recoit jamais la video : l'atelier l'envoie lui-meme a YouTube (`videos.insert`, en morceaux de 16 Mio)
 * avec un acces d'une heure que Castellan lui remet, et ne lui doit que la ligne de ce best-of nomme
 * (`bestof_envois`), qui tient « jamais de doublon ». Routes `POST /api/atelier/bestof...`, backend
 * `bestof/routes.ts`. Une reponse qui porte un `acces` part avec `Cache-Control: no-store`.
 */

/** La cle d'un best-of, choisie par l'atelier (`it-takes-two`) : celle de la contrainte de `bestof_envois.cle`. */
export const CLE_BESTOF = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * La cause d'un echec : le code de l'issue (`youtube_session_perdue`), jamais un texte libre. Une lettre minuscule,
 * puis des minuscules, des chiffres ou des soulignes, 64 caracteres au plus (la forme du code d'une `ErreurReseau`).
 * Rien d'autre ne se range dans `bestof_envois.cause`, pas meme par erreur un acces : un autre texte est refuse en 400.
 */
export const CODE_CAUSE_BESTOF = /^[a-z][a-z0-9_]{0,63}$/;

/** Les etats d'un envoi de best-of, ceux de la contrainte de `bestof_envois.etat` (`migrations/1013_bestof_envois.sql`). */
export const ETATS_BESTOF = ["en_cours", "programme", "echec", "a_verifier"] as const;
export const etatBestofSchema = z.enum(ETATS_BESTOF);
export type EtatBestof = z.infer<typeof etatBestofSchema>;

/** La longueur d'un titre YouTube, en points de code : celle de la contrainte de `bestof_envois.titre`. */
const TITRE_BESTOF_MAX = 100;
/** La taille maximale d'une video YouTube : 256 Gio. */
const TAILLE_BESTOF_MAX = 274_877_906_944;
/** Un SHA-256 en 64 chiffres hexadecimaux minuscules, comme `bestof_envois.empreinte`. */
const EMPREINTE_BESTOF = /^[0-9a-f]{64}$/;
/** L'identifiant d'une video YouTube : celui de la contrainte de `bestof_envois.id_video`. */
const ID_VIDEO_BESTOF = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * Le dernier instant que le texte ISO d'une date garde a quatre chiffres : le 31 decembre 9999, a la
 * milliseconde. Un fuseau peut sortir de la plage une date qui y tient en apparence, et la base ne lit plus
 * le texte qui en resulte.
 */
const DERNIER_INSTANT_MS = Date.UTC(9999, 11, 31, 23, 59, 59, 999);

/**
 * Le corps de `POST /api/atelier/bestof` : le best-of que l'atelier va envoyer. `cle` le nomme (`CLE_BESTOF`) ;
 * `titre` a de 1 a 100 points de code ; `moment` est une date ISO 8601 avec fuseau (`Z` ou `+01:00`), l'instant ou
 * YouTube publie la video (la route refuse un moment a moins de 15 minutes, `moment_trop_proche`, avec l'horloge du
 * serveur) ; `taille` est un entier d'octets de 1 a 256 Gio ; `empreinte` est le SHA-256 du fichier.
 *
 * **Le titre est juge en deux temps.** Ce schema ne tient que sa longueur, celle de la base. Le reste est ce que YouTube
 * refuse ou ne montre pas : une seule ligne, au moins un caractere visible, ni `<` ni `>`, aucun controle ni formatage
 * invisible hors les liants U+200C et U+200D et les selecteurs de variante. La route (`bestof/routes.ts`) l'ajoute avec
 * la classe de `reseaux/texte.ts`, celle des Shorts : ce paquet n'importe pas le backend, et la classe ne se recopie pas.
 */
export const ouvertureBestofSchema = z.object({
  cle: z.string().regex(CLE_BESTOF),
  titre: z
    .string()
    .min(1)
    .refine((titre) => Array.from(titre).length <= TITRE_BESTOF_MAX, {
      message: `Un titre compte ${TITRE_BESTOF_MAX} points de code au plus.`,
    }),
  moment: z.iso.datetime({ offset: true }).refine(
    (moment) => {
      const instant = Date.parse(moment);
      return !Number.isNaN(instant) && instant <= DERNIER_INSTANT_MS;
    },
    { message: "Date illisible ou trop lointaine." },
  ),
  taille: z.number().int().min(1).max(TAILLE_BESTOF_MAX),
  empreinte: z.string().regex(EMPREINTE_BESTOF),
});
export type OuvertureBestof = z.infer<typeof ouvertureBestofSchema>;

/**
 * Un envoi de best-of, tel que Castellan le rend. `moment`, `creeLe` et `majLe` sont des dates ISO 8601 (UTC,
 * a la milliseconde) ; `idVideo` n'est pose qu'une fois la video programmee chez YouTube (etat `programme`),
 * `miniature` qu'une fois posee par l'atelier ; `essais` compte les ouvertures (1, puis un de plus a chaque
 * reouverture apres un echec certain) ; `cause` est le code (`CODE_CAUSE_BESTOF`) du dernier echec ou de la derniere
 * incertitude, `null` tant qu'aucun n'est arrive.
 */
export const envoiBestofSchema = z.object({
  id: z.string(),
  cle: z.string(),
  etat: etatBestofSchema,
  titre: z.string(),
  moment: z.string(),
  taille: z.number(),
  empreinte: z.string(),
  idVideo: z.string().nullable(),
  miniature: z.boolean(),
  essais: z.number().int().positive(),
  cause: z.string().nullable(),
  creeLe: z.string(),
  majLe: z.string(),
});
export type EnvoiBestof = z.infer<typeof envoiBestofSchema>;

/**
 * Un acces YouTube que Castellan remet a l'atelier : `jeton` (l'acces de Google, qui vit une heure) et
 * `expireLe` (ISO 8601, UTC, a la milliseconde). Le jeton de renouvellement ne quitte jamais Castellan.
 * Toute reponse qui porte un acces part avec `Cache-Control: no-store`.
 */
export const accesYoutubeSchema = z.object({ jeton: z.string(), expireLe: z.string() });
export type AccesYoutube = z.infer<typeof accesYoutubeSchema>;

/** La reponse de `POST /api/atelier/bestof` : l'envoi cree ou rouvert, et un acces neuf. */
export const reponseOuvertureBestofSchema = z.object({
  envoi: envoiBestofSchema,
  acces: accesYoutubeSchema,
});

/** La reponse de `POST /api/atelier/bestof/{id}/acces` : un acces neuf. */
export const reponseAccesBestofSchema = z.object({ acces: accesYoutubeSchema });

/** Le corps de `POST /api/atelier/bestof/{id}/video` : l'identifiant que YouTube a donne a la video. */
export const videoBestofSchema = z.object({ idVideo: z.string().regex(ID_VIDEO_BESTOF) });

/**
 * Le corps de `POST /api/atelier/bestof/{id}/echec` : `cause`, le code de l'issue ecrit par l'atelier
 * (`youtube_session_perdue`, `CODE_CAUSE_BESTOF`), jamais un texte libre ; et `incertain` : la video existe
 * peut-etre chez YouTube (une coupure en plein envoi). Un echec incertain ne redevient jamais un echec certain.
 */
export const echecBestofSchema = z.object({
  cause: z.string().regex(CODE_CAUSE_BESTOF, {
    message:
      "Un code : une lettre minuscule, puis des minuscules, des chiffres ou des soulignes (64 au plus).",
  }),
  incertain: z.boolean(),
});
export type EchecBestof = z.infer<typeof echecBestofSchema>;

/** La reponse de `video`, `miniature` et `echec` : l'envoi tel qu'il est maintenant. */
export const reponseEnvoiBestofSchema = z.object({ envoi: envoiBestofSchema });

/**
 * Une tache vue par la regie : tout sauf ce que l'agent lit (`entree`) et ce qu'il a produit
 * (`sortie`), qui peuvent peser des dizaines de Ko et n'ont rien a faire a l'ecran. `cause` est celle
 * du dernier echec ; `finieLe` n'est pose que pour une tache faite ou en echec.
 */
export const resumeTacheSchema = z.object({
  id: z.string(),
  type: typeTacheSchema,
  cle: z.string(),
  etat: etatTacheSchema,
  essais: z.number().int().nonnegative(),
  cause: z.string().nullable(),
  creeLe: z.string(),
  finieLe: z.string().nullable(),
});
export type ResumeTache = z.infer<typeof resumeTacheSchema>;

/** Le dernier signe de vie de l'agent. `vuLe` est une date ISO 8601 (UTC). */
export const agentSchema = z.object({
  vuLe: z.string(),
  version: z.string(),
  etat: z.string(),
  detail: z.string().nullable(),
});
export type AgentAtelier = z.infer<typeof agentSchema>;

/**
 * La pause de la file : elle ne rend aucune tache tant qu'un stream est ouvert (`streams.fin` vide), et
 * `depuis` est le debut du plus ancien de ces streams (date ISO 8601, UTC). Un direct reste ouvert a tort
 * apres une longue panne arreterait la file sans bruit : la carte « Agent du PC » le dit.
 */
export const pauseFileSchema = z.object({ depuis: z.string() });
export type PauseFile = z.infer<typeof pauseFileSchema>;

/**
 * Reponse de `GET /api/diffusion/atelier`, la carte « Agent du PC » de la regie.
 *
 * - `agent` : le dernier signe de vie, ou `null` tant que l'agent ne s'est jamais manifeste ;
 * - `taches` : les vingt dernieres taches creees, de tout etat, la plus recente d'abord ;
 * - `echecs` : les vingt dernieres taches en echec pour de bon, la plus recemment finie d'abord. Une
 *   liste a part : un echec ancien ne doit pas disparaitre sous les taches creees depuis ;
 * - `pause` : `null` quand la file travaille, sinon depuis quand elle attend la fin d'un direct. Le
 *   serveur la dit toujours, et la regie l'exige : une reponse sans `pause` n'est pas celle de ce
 *   serveur, elle ne se lit pas comme « la file travaille ».
 */
export const etatAtelierSchema = z.object({
  agent: agentSchema.nullable(),
  taches: z.array(resumeTacheSchema),
  echecs: z.array(resumeTacheSchema),
  pause: pauseFileSchema.nullable(),
});
export type EtatAtelier = z.infer<typeof etatAtelierSchema>;
